import { defineMessages, useIntl } from 'react-intl';
import { Button } from '@cbhq/cds-web/buttons';
import { HeroSquare } from '@cbhq/cds-web/illustrations';
import { Box, VStack } from '@cbhq/cds-web/layout';
import { TextBody, TextTitle1 } from '@cbhq/cds-web/typography';
import { NetworkError } from '@cbhq/data-layer';

import { retailUrl } from '../../configs/environments';

import { useLogErrorView } from './useLogErrorView';

const messages = defineMessages({
  connectionTrouble: {
    id: 'ErrorStates.connectionTrouble',
    defaultMessage: `We're having connection issues`,
    description: 'Fallback message to handle generic error cases',
  },
  tryAgainLater: {
    id: 'ErrorStates.tryAgainLater',
    defaultMessage: 'Please try again later today.',
    description: 'Fallback message to handle generic error cases',
  },
  networkTitle: {
    id: 'ErrorStates.networkTitle',
    defaultMessage: "You're offline",
    description: 'Title shown when the browser cannot reach the server',
  },
  networkBody: {
    id: 'ErrorStates.networkBody',
    defaultMessage: 'Check your internet connection and try again.',
    description: 'Body shown when the browser cannot reach the server',
  },
  goToCoinbase: {
    id: 'ErrorPages.goToCoinbase',
    defaultMessage: `Go to Coinbase.com`,
    description: 'Giving direction or user action',
  },
  retry: {
    id: 'ErrorPages.retry',
    defaultMessage: 'Retry',
    description: 'Button to retry a failed action',
  },
});

type Props = {
  retry?: () => void;
  error?: Error;
};

export function BasicErrorFallback({ retry, error }: Props) {
  const { formatMessage } = useIntl();
  const isNetworkError = error instanceof NetworkError;
  let illustrationName: 'networkWarning' | 'errorRefreshWeb' | 'errorWeb500';
  let logMessage: string;
  let title: string;
  let body: string;
  let buttonComponent: React.ReactNode | null;

  if (isNetworkError) {
    illustrationName = 'networkWarning';
    logMessage = 'network_error_fallback';
    title = formatMessage(messages.networkTitle);
    body = formatMessage(messages.networkBody);
    buttonComponent = null;
  } else if (retry != null) {
    illustrationName = 'errorRefreshWeb';
    logMessage = 'basic_error_fallback';
    title = formatMessage(messages.connectionTrouble);
    body = formatMessage(messages.tryAgainLater);
    buttonComponent = (
      <Button onClick={retry} block>
        {formatMessage(messages.retry)}
      </Button>
    );
  } else {
    illustrationName = 'errorWeb500';
    logMessage = 'basic_error_fallback';
    title = formatMessage(messages.connectionTrouble);
    body = formatMessage(messages.tryAgainLater);
    buttonComponent = (
      <Button href={retailUrl} as="a" block>
        {formatMessage(messages.goToCoinbase)}
      </Button>
    );
  }

  useLogErrorView(logMessage, error?.message);

  return (
    <VStack alignItems="center" width="100%" paddingX={3} paddingY={10} height="100%">
      <VStack gap={3} alignItems="center" width="100%" maxWidth={680}>
        <Box paddingBottom={4}>
          <HeroSquare name={illustrationName} dimension="200x200" />
        </Box>
        <TextTitle1 textAlign="center" as="h1">
          {title}
        </TextTitle1>
        <TextBody as="p" color="fgMuted">
          {body}
        </TextBody>
        <Box paddingTop={2} width="100%">
          {buttonComponent}
        </Box>
      </VStack>
    </VStack>
  );
}
